import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import CreateTeamDialog from './components/CreateTeamDialog'
import DisbandTeamDialog from './components/DisbandTeamDialog'
import EditTeamDialog from './components/EditTeamDialog'
import RemoveMemberDialog from './components/RemoveMemberDialog'
import TeamCard from './components/TeamCard'
import { isGeneralTeam } from './utils'

import styles from './Department.module.scss'

import type { EnterpriseMemberRowResponse, TeamResponse } from '@/api/generated'
import Button from '@/components/Button'
import FormErrorBanner from '@/components/FormErrorBanner'
import { useEnterpriseTeams } from '@/hooks/useEnterpriseTeams'
import { selectCurrentEnterprise } from '@/store/features/user/userSlice'

export default function Department() {
  const enterprise = useSelector(selectCurrentEnterprise)
  const enterpriseId = enterprise?.id ?? ''

  const [isCreateDialogOpen, setCreateDialogOpen] = useState(false)
  const [editingTeam, setEditingTeam] = useState<TeamResponse | null>(null)
  const [disbandingTeam, setDisbandingTeam] = useState<TeamResponse | null>(null)
  const [removingMember, setRemovingMember] = useState<{
    team: TeamResponse
    member: EnterpriseMemberRowResponse
  } | null>(null)

  const { data: teams = [], isLoading: isLoadingTeams, error: teamsError } = useEnterpriseTeams(enterpriseId)

  // Sort: General teams last so the user-named departments lead.
  const sortedTeams = useMemo(() => {
    const copy = [...teams]
    copy.sort((a, b) => {
      const aGen = isGeneralTeam(a)
      const bGen = isGeneralTeam(b)
      if (aGen && !bGen) return 1
      if (!aGen && bGen) return -1
      return a.name.localeCompare(b.name)
    })
    return copy
  }, [teams])

  // "Has departments" means at least one non-General team exists — the empty
  // state should still render even if every cost center seeded a General team.
  const hasUserDepartments = useMemo(() => sortedTeams.some((t) => !isGeneralTeam(t)), [sortedTeams])

  if (!enterprise) return null

  return (
    <div className={styles.container}>
      {teamsError && <FormErrorBanner message="Failed to load departments." />}

      {isLoadingTeams ? (
        <div className={styles.placeholder}>Loading departments…</div>
      ) : teamsError ? null : !hasUserDepartments ? (
        <div className={styles.emptyState}>
          <span className={styles.emptyTitle}>No departments</span>
          <p className={styles.emptySubtitle}>
            Create departments to organize your team and view usage data by group (e.g. finance, product, marketing).
          </p>
          <Button title="Create a department" onClick={() => setCreateDialogOpen(true)} type="dark" />
        </div>
      ) : (
        <>
          <div className={styles.actions}>
            <Button title="Create a department" onClick={() => setCreateDialogOpen(true)} type="dark" />
          </div>
          <div className={styles.cardList}>
            {sortedTeams.map((team) => (
              <TeamCard
                key={team.id}
                team={team}
                onEdit={setEditingTeam}
                onDisband={setDisbandingTeam}
                onRemoveMember={(t, member) => setRemovingMember({ team: t, member })}
              />
            ))}
          </div>
        </>
      )}

      <CreateTeamDialog
        isOpen={isCreateDialogOpen}
        enterpriseId={enterprise.id}
        onSave={() => setCreateDialogOpen(false)}
        onCancel={() => setCreateDialogOpen(false)}
      />

      <EditTeamDialog
        isOpen={!!editingTeam}
        team={editingTeam}
        allTeams={teams}
        enterpriseId={enterprise.id}
        onClose={() => setEditingTeam(null)}
      />

      <DisbandTeamDialog
        isOpen={!!disbandingTeam}
        team={disbandingTeam}
        allTeams={teams}
        enterpriseName={enterprise.name}
        enterpriseId={enterprise.id}
        onClose={() => setDisbandingTeam(null)}
      />

      <RemoveMemberDialog
        isOpen={!!removingMember}
        sourceTeam={removingMember?.team ?? null}
        allTeams={teams}
        member={removingMember ? { userId: removingMember.member.userId, email: removingMember.member.email } : null}
        enterpriseName={enterprise.name}
        enterpriseId={enterprise.id}
        onClose={() => setRemovingMember(null)}
      />
    </div>
  )
}
